/**
 * /books — full curated shelf.
 * Filterable by category. Featured + Centennial-authored books surface first.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2, ExternalLink, Sparkles } from "lucide-react";
import { fetchBooks, trackBookClick } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import useSEO from "@/lib/useSEO";

const CATEGORIES = [
  { key: "", label: "All" },
  { key: "wealth", label: "Wealth" },
  { key: "retirement", label: "Retirement" },
  { key: "saving", label: "Saving" },
  { key: "budget", label: "Budget" },
  { key: "youth", label: "Youth" },
];

export default function Books() {
  useSEO({
    title: "Books · Curated reading on wealth, retirement, saving · Centennial",
    description: "A hand-picked shelf of the best books on wealth, retirement planning, saving, budgeting, and teaching young people about money.",
    canonicalPath: "/books",
  });

  const [category, setCategory] = useState("");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetchBooks(category || undefined)
      .then((d) => setBooks(d.books || []))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, [category]);

  const featured = useMemo(() => books.filter((b) => b.is_featured), [books]);
  const rest = useMemo(() => books.filter((b) => !b.is_featured), [books]);

  const click = async (e, b) => {
    e.preventDefault();
    track(EVENTS.BOOK_CLICK, { book_id: b.book_id, is_centennial: !!b.is_centennial, surface: "books_page" });
    // Centennial-authored ebook: internal route, no outbound.
    if (b.is_centennial) {
      navigate("/ebook/100-year-retirement-plan");
      return;
    }
    try {
      const { url } = await trackBookClick(b.book_id);
      track(EVENTS.BOOK_PURCHASE_OUTBOUND, { book_id: b.book_id, surface: "books_page" });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* noop */
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8"
        >
          <ArrowLeft size={14} /> Back to calculator
        </Link>

        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-2">
          <BookOpen size={12} /> Curated shelf
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium text-[#1C2B23] mb-4">
          Books that changed<br />how we think about money.
        </h1>
        <p className="text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          Hand-picked reading on wealth, retirement, saving, budgeting, and teaching young
          people about money. Every book here has moved someone we know from &ldquo;confused&rdquo; to
          &ldquo;actually in control&rdquo;.
        </p>

        {/* Category tabs */}
        <div className="mt-8 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key || "all"}
              data-testid={`book-cat-${c.key || "all"}`}
              type="button"
              onClick={() => setCategory(c.key)}
              className={`text-xs px-3.5 py-1.5 rounded-full transition-all ${
                category === c.key
                  ? "bg-[#2B5339] text-white"
                  : "bg-white border border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339] hover:text-[#2B5339]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-sm text-[#5C6B62]">
            <Loader2 size={14} className="animate-spin" /> Loading shelf…
          </div>
        ) : books.length === 0 ? (
          <div className="mt-10 text-sm text-[#5C6B62]">
            No books in this category yet — check back soon.
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <section className="mt-10">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#2B5339] font-semibold mb-4 inline-flex items-center gap-1.5">
                  <Sparkles size={11} /> Editor&apos;s picks
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {featured.map((b) => (
                    <BookCard key={b.book_id} b={b} onClick={click} large />
                  ))}
                </div>
              </section>
            )}

            {rest.length > 0 && (
              <section className="mt-12">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-4">
                  More on the shelf
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rest.map((b) => (
                    <BookCard key={b.book_id} b={b} onClick={click} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <div className="mt-16 bg-[#E8F0EB] border border-[#2B5339] rounded-2xl p-6 max-w-3xl">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
            Are you an author?
          </div>
          <p className="text-sm text-[#1C2B23] leading-relaxed">
            We&apos;re opening the shelf to independent authors of personal-finance books.
            Submit your ebook — approved authors keep 70% of every sale, we handle
            checkout and traffic.
          </p>
          <Link
            to="/authors/submit"
            data-testid="authors-submit-cta"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-[#2B5339] hover:underline"
          >
            Submit your book →
          </Link>
        </div>

        <p className="text-[11px] text-[#5C6B62] mt-8 italic">
          Links on this page are affiliate links. Centennial earns a small commission on purchases at
          no extra cost to you. We only recommend books we&apos;ve personally read.
        </p>
      </div>
    </div>
  );
}

function BookCard({ b, onClick, large }) {
  return (
    <a
      data-testid={`book-${b.book_id}`}
      href="#"
      onClick={(e) => onClick(e, b)}
      className={`group flex gap-4 bg-white border border-[#E5E2DC] rounded-xl p-4 hover:border-[#2B5339] hover:shadow-sm transition-all ${large ? "" : ""}`}
    >
      {b.cover_url && (
        <img
          src={b.cover_url}
          alt={b.title}
          className={`${large ? "w-20 h-28" : "w-16 h-22"} object-cover rounded-md shadow-sm flex-shrink-0`}
          onError={(e) => {
            if (!e.target.dataset.fallback) {
              e.target.dataset.fallback = "1";
              e.target.src = "/book-placeholder.svg";
            }
          }}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-[#1C2B23] leading-tight group-hover:text-[#2B5339] ${large ? "text-lg" : "text-sm"}`}>
          {b.title}
          {b.is_centennial && (
            <span className="ml-2 text-[9px] tracking-[0.14em] uppercase font-semibold text-[#2B5339] bg-[#F0EFEA] rounded-full px-2 py-0.5 align-middle">
              ours
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#5C6B62] mt-1">{b.author}</div>
        {b.blurb && (
          <p className={`text-xs text-[#1C2B23] mt-2 leading-relaxed ${large ? "" : "line-clamp-3"}`}>
            {b.blurb}
          </p>
        )}
        <div className="mt-3 flex items-center gap-3 text-[11px]">
          {b.price_hint && <span className="text-[#2B5339] font-medium">{b.price_hint}</span>}
          <span className="text-[#5C6B62] group-hover:text-[#2B5339] inline-flex items-center gap-1">
            View <ExternalLink size={9} />
          </span>
        </div>
      </div>
    </a>
  );
}
